// src/screens/BadgesLeaderboardScreen.js

import React, { useEffect, useMemo, useState } from 'react';
import './BadgesLeaderboardScreen.css';
import api from '../api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import backIcon from '../assets/backspace-icon.png';
import { BADGES, BADGE_DISPLAY } from '../constants/badges';

// Drawers
import BarterKingDrawer from '../components/popups/BarterKingDrawer';
import MerchantStarDrawer from '../components/popups/MerchantStarDrawer';
import CreatorFlameDrawer from '../components/popups/CreatorFlameDrawer';
import PiGuardianDrawer from '../components/popups/PiGuardianDrawer';

import SkeletonBadgesLeaderboardRow from '../components/skeleton/SkeletonBadgesLeaderboardRow';
import gainIcon from '../assets/gain-icon.png';
import lostIcon from '../assets/lost-icon.png';
import infoIcon from '../assets/info-icon.png';

// Optional tiny avatar helper with fallback
const Avatar = ({ src, alt }) => {
  const fallback = '/placeholder-profile.png';
  const imgSrc = src || fallback;

  return (
    <img
      className="blb-avatar"
      src={imgSrc}
      alt={alt || ''}
      loading="lazy"
    />
  );
};

// These badge categories are currently hidden.
const MAINTENANCE_BADGES = [
  'COUNCIL',
  'PI_PIONEER',
  'INNOVATOR',
];

// Number of positions that receive each badge.
const AWARD_CUTOFF = {
  BARTER_KING: 1,
  MERCHANT_STAR: 3,
  CREATOR_FLAME: 5,
  PI_GUARDIAN: 10,
};

const BADGE_TAB_LABELS = {
  BARTER_KING: 'King',
  MERCHANT_STAR: 'Merchant',
  CREATOR_FLAME: 'Creator',
  PI_GUARDIAN: 'Guardian',
};

function msToParts(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  return {
    days,
    hours,
    mins,
    secs,
  };
}

function formatCountdown(ms) {
  const { days, hours, mins } = msToParts(ms);

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }

  return `${mins}m`;
}

function getBadgeTrendMeta(row) {
  const meta = row?.meta || {};

  const trend = String(
    meta.engagementTrend ||
    meta.scoreTrend ||
    ''
  ).toLowerCase();

  if (trend === 'up') {
    return {
      icon: gainIcon,
      alt: 'Score increased',
    };
  }

  if (trend === 'down') {
    return {
      icon: lostIcon,
      alt: 'Score decreased',
    };
  }

  return null;
}

function BadgesLeaderboardScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL is the source of truth for the selected tab.
  const urlTab = searchParams.get('tab');

  const defaultTab =
    window.localStorage.getItem('badges_active_tab') ||
    BADGES[0].id;

  const activeId = urlTab || defaultTab;

  const HIDDEN_TAB_SET = useMemo(
    () =>
      new Set([
        'COUNCIL',
        'PI_PIONEER',
        'INNOVATOR',
      ]),
    []
  );

  const firstVisibleBadgeId = useMemo(() => {
    const first = BADGES.find(
      (badge) => !HIDDEN_TAB_SET.has(badge.id)
    );

    return first?.id || BADGES[0]?.id;
  }, [HIDDEN_TAB_SET]);

  const safeActiveId = HIDDEN_TAB_SET.has(activeId)
    ? firstVisibleBadgeId
    : activeId;

  // Set the initial tab in the URL.
  useEffect(() => {
    if (!urlTab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', safeActiveId);
      setSearchParams(next, { replace: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Replace hidden badge tabs with the first visible badge.
  useEffect(() => {
    if (
      urlTab &&
      HIDDEN_TAB_SET.has(urlTab)
    ) {
      const next = new URLSearchParams(searchParams);

      next.set('tab', safeActiveId);
      setSearchParams(next, { replace: true });

      window.localStorage.setItem(
        'badges_active_tab',
        safeActiveId
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTab, safeActiveId]);

  useEffect(() => {
    if (safeActiveId) {
      window.localStorage.setItem(
        'badges_active_tab',
        safeActiveId
      );
    }
  }, [safeActiveId]);

  const [dataByBadge, setDataByBadge] = useState({});
  const [loadingByBadge, setLoadingByBadge] = useState({});
  const [errorByBadge, setErrorByBadge] = useState({});
  const [metricsByUser, setMetricsByUser] = useState({});

  const [showBKDrawer, setShowBKDrawer] = useState(false);
  const [showMSDrawer, setShowMSDrawer] = useState(false);
  const [showCFDrawer, setShowCFDrawer] = useState(false);
  const [showPGDrawer, setShowPGDrawer] = useState(false);

  const openActiveBadgeDrawer = () => {
    if (safeActiveId === 'BARTER_KING') {
      setShowBKDrawer(true);
    } else if (safeActiveId === 'MERCHANT_STAR') {
      setShowMSDrawer(true);
    } else if (safeActiveId === 'CREATOR_FLAME') {
      setShowCFDrawer(true);
    } else if (safeActiveId === 'PI_GUARDIAN') {
      setShowPGDrawer(true);
    }
  };

  // Reward-cycle countdown
  const [cycleInfo, setCycleInfo] = useState(null);
  const [cycleErr, setCycleErr] = useState('');
  const [remainingMs, setRemainingMs] = useState(null);

  const activeMeta = useMemo(
    () => BADGE_DISPLAY[safeActiveId] || {},
    [safeActiveId]
  );

  const activeAwardCutoff =
    AWARD_CUTOFF[safeActiveId] || 0;

  const defaultWindow =
    `${cycleInfo?.windowDays || 60}d`;

  const badgeTabs = useMemo(() => {
    return BADGES
      .filter(
        (badge) => !HIDDEN_TAB_SET.has(badge.id)
      )
      .map((badge) => {
        const display =
          BADGE_DISPLAY[badge.id] || {};

        return {
          key: badge.id,
          label:
            BADGE_TAB_LABELS[badge.id] ||
            badge.label,
          fullLabel: badge.label,
          icon: display.icon || null,
          emoji: display.emoji || '',
        };
      });
  }, [HIDDEN_TAB_SET]);

  const fetchRewardCycle = async () => {
    try {
      setCycleErr('');

      const res = await api.get('/rewards/cycle');
      const cycle = res?.data || {};

      if (!cycle?.endAt) {
        throw new Error('Missing endAt');
      }

      setCycleInfo(cycle);

      const nowMs = new Date(
        cycle.now || Date.now()
      ).getTime();

      const endMs = new Date(
        cycle.endAt
      ).getTime();

      setRemainingMs(
        Math.max(0, endMs - nowMs)
      );
    } catch (error) {
      setCycleErr(
        'Failed to load reward cycle.'
      );
      setCycleInfo(null);
      setRemainingMs(null);
    }
  };

  useEffect(() => {
    fetchRewardCycle();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!cycleInfo?.endAt) return undefined;

    const endMs = new Date(
      cycleInfo.endAt
    ).getTime();

    const timer = setInterval(() => {
      const difference = endMs - Date.now();
      const next = Math.max(0, difference);

      setRemainingMs(next);

      if (next <= 0) {
        fetchRewardCycle();
      }
    }, 1000);

    return () => clearInterval(timer);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleInfo?.endAt]);

  const fetchMerchantMetricsFor = async (
    usernames = []
  ) => {
    const pending = usernames.filter(
      (username) => !metricsByUser[username]
    );

    if (!pending.length) return;

    try {
      const results = await Promise.all(
        pending.map(async (username) => {
          try {
            const response = await api.get(
              `/merchants/metrics/${username}`
            );

            return [
              username,
              {
                completedOrders: Number(
                  response.data?.completedOrders ?? 0
                ),
                avgRating: Number(
                  response.data?.avgRating ?? 0
                ),
              },
            ];
          } catch {
            return [
              username,
              {
                completedOrders: 0,
                avgRating: 0,
              },
            ];
          }
        })
      );

      setMetricsByUser((previous) => {
        const next = { ...previous };

        results.forEach(
          ([username, metrics]) => {
            next[username] = metrics;
          }
        );

        return next;
      });
    } catch {
      // Ignore metric-loading errors.
    }
  };

  const fetchBadge = async (badgeId) => {
    if (MAINTENANCE_BADGES.includes(badgeId)) {
      setDataByBadge((current) => ({
        ...current,
        [badgeId]: [],
      }));

      setLoadingByBadge((current) => ({
        ...current,
        [badgeId]: false,
      }));

      setErrorByBadge((current) => ({
        ...current,
        [badgeId]: null,
      }));

      return;
    }

    if (loadingByBadge[badgeId]) return;

    setLoadingByBadge((current) => ({
      ...current,
      [badgeId]: true,
    }));

    setErrorByBadge((current) => ({
      ...current,
      [badgeId]: null,
    }));

    try {
      let viewLimit;

      if (badgeId === 'PI_GUARDIAN') {
        viewLimit = 100;
      } else if (
        badgeId === 'BARTER_KING' ||
        badgeId === 'MERCHANT_STAR' ||
        badgeId === 'CREATOR_FLAME'
      ) {
        viewLimit = 50;
      } else {
        viewLimit =
          (BADGE_DISPLAY[badgeId]?.maxSupply ??
            50) ||
          50;
      }

      const url =
        `/badges/leaderboard?type=${badgeId}` +
        `&limit=${viewLimit}`;

      const response = await api.get(url);
      const payload = response?.data ?? null;

      const badgeRows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.items)
          ? payload.items
          : [];

      setDataByBadge((current) => ({
        ...current,
        [badgeId]: badgeRows,
      }));

      if (
        badgeId === 'MERCHANT_STAR' &&
        badgeRows.length
      ) {
        const usernames = badgeRows.map(
          (row) => row.username
        );

        fetchMerchantMetricsFor(usernames);
      }
    } catch (error) {
      setErrorByBadge((current) => ({
        ...current,
        [badgeId]: 'Failed to load',
      }));
    } finally {
      setLoadingByBadge((current) => ({
        ...current,
        [badgeId]: false,
      }));
    }
  };

  useEffect(() => {
    if (
      !dataByBadge[safeActiveId] &&
      !loadingByBadge[safeActiveId]
    ) {
      fetchBadge(safeActiveId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeActiveId]);

  const dateStr = (value) => {
    if (!value) return null;

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    const day = String(
      date.getDate()
    ).padStart(2, '0');

    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const month =
      monthNames[date.getMonth()];

    const yearShort = String(
      date.getFullYear()
    ).slice(-2);

    let hours = date.getHours();

    const minutes = String(
      date.getMinutes()
    ).padStart(2, '0');

    const ampm =
      hours >= 12 ? 'PM' : 'AM';

    hours %= 12;

    if (hours === 0) {
      hours = 12;
    }

    return (
      `${day} ${month} ${yearShort} , ` +
      `${hours}:${minutes} ${ampm}`
    );
  };

  const formatStat = (badgeId, row) => {
    const meta = row?.meta || {};

    const number = (value) =>
      typeof value === 'number'
        ? value
        : Number(value || 0);

    switch (badgeId) {
      case 'BARTER_KING': {
        const total = number(meta.totalOut);

        return `Outflow: ${total.toFixed(2)} Pi`;
      }

      case 'MERCHANT_STAR': {
        const sales = number(
          meta.sales ?? row.score ?? 0
        ).toFixed(2);

        const completed =
          metricsByUser[
            row.username
          ]?.completedOrders;

        const orders = Number.isFinite(completed)
          ? completed
          : number(meta.orders);

        return (
          `Sales: ${sales} Pi • ` +
          `Orders: ${orders}`
        );
      }

      case 'CREATOR_FLAME': {
        const score = number(
          meta.engagementScore
        );

        const displayScore =
          Number.isFinite(score)
            ? score.toFixed(4)
            : '0.0000';

        return (
          `Engagement score: ${displayScore}`
        );
      }

      case 'PI_GUARDIAN': {
        const days = number(
          meta.daysActive ??
          meta.daysActive60d ??
          meta.daysActive30d ??
          meta.daysActive7d ??
          0
        );

        const pings = number(
          meta.pings ??
          meta.pings60d ??
          meta.pings30d ??
          meta.pings7d ??
          0
        );

        return (
          `Active days: ${days} • ` +
          `Pings: ${pings}`
        );
      }

      case 'COUNCIL':
        return (
          `Votes: ${number(meta.votes)} • ` +
          `Proposals: ${number(meta.proposals)}`
        );

      case 'PI_PIONEER':
        return (
          `Retained: ${number(meta.retained)} • ` +
          `Refs: ${number(meta.refs)}`
        );

      case 'INNOVATOR':
        return 'Awarded by admin';

      default:
        return '';
    }
  };

  const rows =
    dataByBadge[safeActiveId] || [];

  const loading =
    Boolean(loadingByBadge[safeActiveId]);

  const error =
    errorByBadge[safeActiveId];

  const isMaintenance =
    MAINTENANCE_BADGES.includes(
      safeActiveId
    );

  const podiumEntries = [
    {
      row: rows[1],
      rank: 2,
      position: 'second',
      medal: '🥈',
    },
    {
      row: rows[0],
      rank: 1,
      position: 'first',
      medal: '🥇',
    },
    {
      row: rows[2],
      rank: 3,
      position: 'third',
      medal: '🥉',
    },
  ].filter((entry) => entry.row);

  const formatPodiumStat = (
    badgeId,
    row
  ) => {
    const meta = row?.meta || {};

    const number = (value) =>
      typeof value === 'number'
        ? value
        : Number(value || 0);

    switch (badgeId) {
      case 'BARTER_KING':
        return (
          `${number(meta.totalOut).toFixed(2)} Pi`
        );

      case 'MERCHANT_STAR':
        return (
          `${number(
            meta.sales ?? row.score
          ).toFixed(2)} Pi`
        );

      case 'CREATOR_FLAME':
        return number(
          meta.engagementScore
        ).toFixed(4);

      case 'PI_GUARDIAN':
        return (
          `${number(
            meta.daysActive ??
            meta.daysActive60d ??
            meta.daysActive30d ??
            meta.daysActive7d
          )} active days`
        );

      default:
        return '';
    }
  };

  const openBadgeDetails = (row) => {
    if (!row?.username) return;

    if (safeActiveId === 'BARTER_KING') {
      navigate(
        `/badges/barter-king/${row.username}` +
        `?window=${defaultWindow}`
      );
    } else if (
      safeActiveId === 'MERCHANT_STAR'
    ) {
      navigate(
        `/badges/merchant-star/${row.username}` +
        `?window=${defaultWindow}`
      );
    } else if (
      safeActiveId === 'CREATOR_FLAME'
    ) {
      navigate(
        `/badges/creator-flame/${row.username}` +
        `?window=${defaultWindow}`
      );
    } else if (
      safeActiveId === 'PI_GUARDIAN'
    ) {
      navigate(
        `/badges/pi-guardian/${row.username}` +
        `?window=${defaultWindow}`
      );
    } else {
      navigate(`/user/${row.username}`);
    }
  };

  return (
    <div className="blb-container">
      <div className="blb-top-wrapper">
        <div className="blb-header">
          <button
            type="button"
            className="blb-header-icon-btn"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <img
              src={backIcon}
              alt=""
              className="blb-back"
            />
          </button>

          <div className="blb-headings">
            <h2 className="blb-title">
              Leaderboard
            </h2>
          </div>

          <button
            type="button"
            className="blb-header-icon-btn"
            onClick={openActiveBadgeDrawer}
            aria-label={
              `Learn about ${
                activeMeta.label ||
                'this badge'
              }`
            }
            title={
              `Learn about ${
                activeMeta.label ||
                'this badge'
              }`
            }
          >
            <img
              src={infoIcon}
              alt=""
              className="blb-info-icon"
            />
          </button>
        </div>

        <div className="blb-tabs-wrap">
          <div
            className="blb-badge-tabs"
            role="tablist"
            aria-label={
              'Badge leaderboard categories'
            }
          >
            {badgeTabs.map((tab) => {
              const isActive =
                safeActiveId === tab.key;

              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-label={tab.fullLabel}
                  className={
                    `blb-badge-tab ${
                      isActive
                        ? 'is-active'
                        : ''
                    }`
                  }
                  onClick={() => {
                    const next =
                      new URLSearchParams(
                        searchParams
                      );

                    next.set(
                      'tab',
                      tab.key
                    );

                    setSearchParams(
                      next,
                      { replace: true }
                    );

                    window.localStorage.setItem(
                      'badges_active_tab',
                      tab.key
                    );
                  }}
                >
                  {tab.icon ? (
                    <img
                      src={tab.icon}
                      alt=""
                      className={
                        'blb-badge-tab-icon'
                      }
                    />
                  ) : (
                    <span
                      className={
                        'blb-badge-tab-emoji'
                      }
                      aria-hidden="true"
                    >
                      {tab.emoji}
                    </span>
                  )}

                  <span
                    className={
                      'blb-badge-tab-label'
                    }
                  >
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div
        className="blb-list-scroll"
        id="blb-scroll"
      >
        <section className="blb-competition">
          <div className="blb-competition-main">
            <div
              className="blb-competition-badge"
            >
              {activeMeta.icon ? (
                <img
                  src={activeMeta.icon}
                  alt=""
                  className={
                    'blb-competition-icon'
                  }
                />
              ) : (
                <span
                  className={
                    'blb-competition-emoji'
                  }
                  aria-hidden="true"
                >
                  {activeMeta.emoji}
                </span>
              )}

              <div
                className={
                  'blb-competition-copy'
                }
              >
                <h3
                  className={
                    'blb-competition-title'
                  }
                >
                  {activeMeta.label ||
                    safeActiveId}
                </h3>

                <p
                  className={
                    'blb-competition-reward'
                  }
                >
                  {activeAwardCutoff === 1
                    ? 'The #1 position earns this badge'
                    : `Top ${activeAwardCutoff} earn this badge`}
                </p>
              </div>
            </div>

            <div
              className={
                'blb-competition-countdown'
              }
            >
              <span
                className={
                  'blb-competition-countdown-label'
                }
              >
                Ends in
              </span>

              {cycleErr ? (
                <span
                  className="blb-cycle-error"
                >
                  Unavailable
                </span>
              ) : remainingMs === null ? (
                <span
                  className="blb-cycle-loading"
                >
                  Loading…
                </span>
              ) : (
                <strong
                  className={
                    'blb-competition-time'
                  }
                >
                  {formatCountdown(
                    remainingMs
                  )}
                </strong>
              )}
            </div>
          </div>

          <div
            className={
              'blb-competition-footer'
            }
          >
            <span>
              {cycleInfo?.windowDays || 60}
              -day competition
            </span>

            <span
              className={
                'blb-competition-dot'
              }
              aria-hidden="true"
            >
              •
            </span>

            <span>Live rankings</span>
          </div>
        </section>

        {loading ? (
          <SkeletonBadgesLeaderboardRow
            count={10}
          />
        ) : error ? (
          <div className="blb-error">
            {error}
          </div>
        ) : rows.length === 0 ? (
          isMaintenance ? (
            <div className="blb-empty">
              Under maintenance.
            </div>
          ) : (
            <div className="blb-empty">
              No holders yet.
            </div>
          )
        ) : (
          <div className="blb-list">
            <section
              className="blb-podium-section"
              aria-label={
                'Top three leaderboard positions'
              }
            >
              <div
                className={
                  'blb-podium-heading'
                }
              >
                Top contenders
              </div>

              <div className="blb-podium">
                {podiumEntries.map(
                  ({
                    row,
                    rank,
                    position,
                    medal,
                  }) => {
                    const isAwarded =
                      activeAwardCutoff > 0 &&
                      rank <=
                        activeAwardCutoff;

                    return (
                      <button
                        key={
                          `podium-${safeActiveId}-` +
                          `${row.username}`
                        }
                        type="button"
                        className={
                          `blb-podium-entry ` +
                          `blb-podium-${position} ` +
                          `${
                            isAwarded
                              ? 'is-awarded'
                              : ''
                          }`
                        }
                        onClick={() =>
                          openBadgeDetails(row)
                        }
                        aria-label={
                          `Rank ${rank}, ` +
                          row.username
                        }
                        style={{
                          '--blb-active-accent':
                            activeMeta.accent ||
                            '#007aff',
                        }}
                      >
                        <span
                          className={
                            'blb-podium-medal'
                          }
                          aria-hidden="true"
                        >
                          {medal}
                        </span>

                        <div
                          className={
                            'blb-podium-avatar-wrap'
                          }
                        >
                          <Avatar
                            src={row.profilePic}
                            alt={row.username}
                          />

                          <span
                            className={
                              'blb-podium-rank'
                            }
                          >
                            {rank}
                          </span>
                        </div>

                        <span
                          className={
                            'blb-podium-username'
                          }
                          title={
                            `@${row.username}`
                          }
                        >
                          @{row.username}
                        </span>

                        <span
                          className={
                            'blb-podium-stat'
                          }
                        >
                          {formatPodiumStat(
                            safeActiveId,
                            row
                          )}
                        </span>
                      </button>
                    );
                  }
                )}
              </div>
            </section>

            {rows
              .slice(3)
              .map((row, listIndex) => {
                const index =
                  listIndex + 3;

                const rankClass =
                  index === 0
                    ? 'rank-1'
                    : index === 1
                      ? 'rank-2'
                      : index === 2
                        ? 'rank-3'
                        : '';

                const cutoff =
                  AWARD_CUTOFF[
                    safeActiveId
                  ] || 0;

                const isAwarded =
                  cutoff > 0 &&
                  index < cutoff;

                const showSeparator =
                  index === cutoff &&
                  rows.length > cutoff;

                const badgeTrend =
                  getBadgeTrendMeta(row);

                return (
                  <React.Fragment
                    key={
                      `${safeActiveId}-` +
                      `${row.username}-` +
                      `${index}`
                    }
                  >
                    {showSeparator && (
                      <div
                        className={
                          'blb-separator'
                        }
                      />
                    )}



<div
  className={
    `blb-row ${rankClass}`
  }
  onClick={() => openBadgeDetails(row)}
  onKeyDown={(event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openBadgeDetails(row);
    }
  }}
  role="button"
  tabIndex={0}
>


                      <div
                        className={
                          'blb-rank-bar'
                        }
                        style={{
                          background:
                            isAwarded
                              ? activeMeta.accent
                              : 'transparent',
                        }}
                      />

                      <div
                        className={
                          'blb-rank-wrap'
                        }
                      >
                        <div
                          className="blb-rank"
                        >
                          {index + 1}
                        </div>

                        {badgeTrend && (
                          <img
                            src={
                              badgeTrend.icon
                            }
                            alt={
                              badgeTrend.alt
                            }
                            className={
                              'blb-rank-trend-icon'
                            }
                          />
                        )}
                      </div>

                      <Avatar
                        src={row.profilePic}
                        alt={row.username}
                      />

                      <div
                        className={
                          'blb-col-main'
                        }
                      >
                        <div
                          className={
                            'blb-name-row'
                          }
                        >
                          <span
                            className={
                              'blb-username'
                            }
                            title={
                              `@${row.username}`
                            }
                          >
                            {row.username}
                          </span>
                        </div>

                        <div
                          className="blb-stat"
                        >
                          {formatStat(
                            safeActiveId,
                            row
                          )}
                        </div>

                        <div
                          className="blb-dates"
                        >
                          {row.earnedAt && (
                            <span>
                              Earned:{' '}
                              {dateStr(
                                row.earnedAt
                              )}
                            </span>
                          )}

                          {row.expiresAt && (
                            <span
                              className="dot"
                            >
                              •
                            </span>
                          )}

                          {row.expiresAt && (
                            <span>
                              Expires:{' '}
                              {dateStr(
                                row.expiresAt
                              )}
                            </span>
                          )}
                        </div>
                      </div>


<div className="blb-col-right">

                        <span
                          className="blb-open"
                        >
                          View
                        </span>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
          </div>
        )}
      </div>

      <BarterKingDrawer
        open={showBKDrawer}
        onClose={() =>
          setShowBKDrawer(false)
        }
      />

      <MerchantStarDrawer
        open={showMSDrawer}
        onClose={() =>
          setShowMSDrawer(false)
        }
      />

      <CreatorFlameDrawer
        open={showCFDrawer}
        onClose={() =>
          setShowCFDrawer(false)
        }
      />

      <PiGuardianDrawer
        open={showPGDrawer}
        onClose={() =>
          setShowPGDrawer(false)
        }
      />
    </div>
  );
}

export default BadgesLeaderboardScreen;
